import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { DashboardSubject } from '../../../ui/views/dashboard/subjects-card/subjects-card.component';
import { SubjectActionCardComponent } from '../../../ui/views/dashboard/subject-action-card/subject-action-card.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';
import { StudentService } from '../../../../core/services/student.service';
import { StudentSubjectProgression } from '../../../../core/interfaces/student-subject-progression';
import { LocalizedLabelService } from '../../../../core/services/utils/localized-label.service';

/** Matière du catalogue de l'élève, enrichie de son état de favori. */
interface SubjectRow extends DashboardSubject {
  subjectId: number;
  isFavorite: boolean;
}

/**
 * Teintes de repli. L'API ne renvoie pas de couleur : on en dérive une, stable
 * par matière, pour que la grille reste lisible sans champ supplémentaire.
 */
const PALETTE = [
  'var(--cpurple1)', 'var(--cblue5)', 'var(--cgreen5)', 'var(--cyellow4)',
  'var(--cred5)', 'var(--cpurple0)', 'var(--cblue4)', 'var(--cgreen4)',
];

@Component({
  selector: 'app-subjects-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SubjectActionCardComponent,
    ErrorStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './subjects-list.component.html',
  styleUrl: './subjects-list.component.css',
})
export class SubjectsListComponent implements OnInit {
  private readonly studentService = inject(StudentService);
  private readonly labels = inject(LocalizedLabelService);

  readonly loading = signal(true);
  readonly errored = signal(false);
  readonly rows = signal<SubjectRow[]>([]);

  /** Identifiants dont la bascule est en cours — évite le double clic. */
  readonly pending = signal<ReadonlySet<number>>(new Set());

  query = '';
  private readonly queryText = signal('');

  set searchQuery(value: string) {
    this.query = value;
    this.queryText.set(value);
  }
  get searchQuery(): string {
    return this.query;
  }

  private readonly matching = computed(() => {
    const q = this.queryText().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter((s) => (s.name ?? '').toLowerCase().includes(q));
  });

  readonly favorites = computed(() => this.matching().filter((s) => s.isFavorite));
  readonly others = computed(() => this.matching().filter((s) => !s.isFavorite));
  readonly total = computed(() => this.matching().length);

  ngOnInit(): void {
    this.load();
  }

  reload(): void {
    this.load();
  }

  isPending(subjectId: number): boolean {
    return this.pending().has(subjectId);
  }

  /**
   * Bascule optimiste : l'étoile réagit tout de suite, et revient en arrière
   * si le serveur refuse. Sans cela, l'élève clique dans le vide le temps de
   * l'aller-retour.
   */
  toggleFavorite(row: SubjectRow, next: boolean): void {
    if (this.isPending(row.subjectId)) return;

    this.setFavoriteLocally(row.subjectId, next);
    this.markPending(row.subjectId, true);

    this.studentService.setSubjectFavorite(row.subjectId, next).subscribe({
      next: (res) => {
        const confirmed = res?.data?.isFavorite ?? next;
        this.setFavoriteLocally(row.subjectId, confirmed);
        this.markPending(row.subjectId, false);
      },
      error: () => {
        this.setFavoriteLocally(row.subjectId, !next);
        this.markPending(row.subjectId, false);
      },
    });
  }

  private setFavoriteLocally(subjectId: number, isFavorite: boolean): void {
    this.rows.update((list) =>
      list.map((s) => (s.subjectId === subjectId ? { ...s, isFavorite } : s)),
    );
  }

  private markPending(subjectId: number, busy: boolean): void {
    this.pending.update((set) => {
      const next = new Set(set);
      if (busy) next.add(subjectId);
      else next.delete(subjectId);
      return next;
    });
  }

  private load(): void {
    this.loading.set(true);
    this.errored.set(false);

    this.studentService.getMySubjectsProgression().subscribe({
      next: (res) => {
        this.rows.set((res?.data ?? []).map((s, i) => this.toRow(s, i)));
        this.loading.set(false);
      },
      error: () => {
        // Une liste vide reste vide : aucune matière de démonstration.
        this.rows.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  private toRow(s: StudentSubjectProgression, index: number): SubjectRow {
    return {
      subjectId: s.subjectId,
      name: this.labels.pickPrefixed(s as unknown as Record<string, unknown>, 'subjectName'),
      // Pas d'icône : `iconUrl` contient des noms Material Symbols que la
      // police FontAwesome du produit ne rend pas. La couleur suffit.
      color: PALETTE[s.subjectId % PALETTE.length] ?? PALETTE[index % PALETTE.length],
      progress: Math.round(s.progressionRate ?? 0),
      isFavorite: s.isFavorite ?? false,
    };
  }
}
